import type { CurrentSession } from '@elite-residence/api-client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useApiClient } from '../api/ApiClientProvider';
import { useLocale } from '../i18n/LocaleProvider';
import { Alert, Field, Loading, useAction } from '../ui/ui';

const INITIAL = ['CASH', 'BANK_TRANSFER', 'CARD', 'PAYMENT_LINK', 'OTHER_NON_CASH'];
export function FinanceReferencePage({ session }: { readonly session: CurrentSession }) {
  const client = useApiClient();
  const queries = useQueryClient();
  const { locale } = useLocale();
  const ar = locale === 'ar';
  const action = useAction();
  const editable = session.permissions.includes('reference_data.manage') && session.scope.type === 'all_branches';
  const methods = useQuery({
    queryKey: ['finance-reference', 'methods'],
    queryFn: async () => {
      const response = await client.listPaymentMethods({});
      if (response.status !== 200) throw new Error('methods_unavailable');
      return response.body.methods;
    },
  });
  const categories = useQuery({
    queryKey: ['expense-categories'],
    queryFn: async () => {
      const response = await client.listExpenseCategories({});
      if (response.status !== 200) throw new Error('categories_unavailable');
      return response.body.categories;
    },
  });
  const emptyMethod = {
    code: '',
    labelAr: '',
    labelEn: '',
    classification: 'non_cash' as 'cash' | 'non_cash',
    disabled: false,
  };
  const [method, setMethod] = useState(emptyMethod);
  const [editingMethod, setEditingMethod] = useState(false);
  const [category, setCategory] = useState({ id: '', code: '', labelAr: '', labelEn: '' });
  const refresh = () => {
    void queries.invalidateQueries();
  };
  if (methods.isPending || categories.isPending) return <Loading />;
  if (methods.isError || categories.isError)
    return (
      <Alert kind="error">
        {ar ? 'تعذر تحميل البيانات المرجعية المالية.' : 'Financial reference data could not be loaded.'}
      </Alert>
    );
  const save = ar ? 'حفظ' : 'Save';
  const edit = ar ? 'تعديل' : 'Edit';
  const stateLabel = ar ? 'معطل' : 'Disabled';
  return (
    <main>
      <h1>{ar ? 'طرق الدفع وفئات المصروفات' : 'Payment methods and expense categories'}</h1>
      {action.error && <Alert kind="error">{action.error}</Alert>}
      <section className="er-card">
        <h2>{ar ? 'طرق الدفع' : 'Payment methods'}</h2>
        <p>
          {ar
            ? 'الطرق الجديدة غير نقدية افتراضياً. اختيار نقدي يؤكد الحركة مباشرة. تبقى التصنيفات الأصلية للطرق الأساسية ثابتة.'
            : 'New methods default to Non-cash. Choosing Cash confirms movements immediately. Initial method classifications remain fixed.'}
        </p>
        {editable && (
          <form
            className="er-actions"
            onSubmit={(event) => {
              event.preventDefault();
              void action.run(async (requestKey) => {
                const response = await client.configurePaymentMethod({
                  headers: { 'idempotency-key': requestKey },
                  body: method,
                });
                if (response.status === 200) {
                  refresh();
                  setMethod(emptyMethod);
                  setEditingMethod(false);
                }
                return response;
              });
            }}
          >
            <Field id="method-code" label={ar ? 'رمز الطريقة' : 'Method code'}>
              <input
                id="method-code"
                required
                value={method.code}
                readOnly={editingMethod}
                onChange={(e) =>
                  setMethod({
                    ...method,
                    code: e.target.value,
                    classification:
                      e.target.value === 'CASH'
                        ? 'cash'
                        : INITIAL.includes(e.target.value)
                          ? 'non_cash'
                          : method.classification,
                  })
                }
              />
            </Field>
            <Field id="method-ar" label={ar ? 'اسم الطريقة بالعربية' : 'Method Arabic label'}>
              <input
                id="method-ar"
                required
                value={method.labelAr}
                onChange={(e) => setMethod({ ...method, labelAr: e.target.value })}
              />
            </Field>
            <Field id="method-en" label={ar ? 'اسم الطريقة بالإنجليزية' : 'Method English label'}>
              <input
                id="method-en"
                required
                value={method.labelEn}
                onChange={(e) => setMethod({ ...method, labelEn: e.target.value })}
              />
            </Field>
            <Field id="method-class" label={ar ? 'التصنيف' : 'Classification'}>
              <select
                id="method-class"
                disabled={INITIAL.includes(method.code)}
                value={method.classification}
                onChange={(e) => setMethod({ ...method, classification: e.target.value as 'cash' | 'non_cash' })}
              >
                <option value="non_cash">{ar ? 'غير نقدي' : 'Non-cash'}</option>
                <option value="cash">{ar ? 'نقدي' : 'Cash'}</option>
              </select>
            </Field>
            <label>
              <input
                type="checkbox"
                checked={method.disabled}
                onChange={(e) => setMethod({ ...method, disabled: e.target.checked })}
              />
              {stateLabel}
            </label>
            <button className="er-button" type="submit" disabled={action.busy}>
              {save}
            </button>
            {editingMethod && (
              <button
                type="button"
                onClick={() => {
                  setMethod(emptyMethod);
                  setEditingMethod(false);
                }}
              >
                {ar ? 'طريقة جديدة' : 'New method'}
              </button>
            )}
          </form>
        )}
        <table className="er-table">
          <thead>
            <tr>
              <th>{ar ? 'الرمز' : 'Code'}</th>
              <th>{ar ? 'الاسم' : 'Label'}</th>
              <th>{ar ? 'التصنيف' : 'Classification'}</th>
              <th>{stateLabel}</th>
              {editable && <th>{edit}</th>}
            </tr>
          </thead>
          <tbody>
            {methods.data.map((row) => (
              <tr key={row.id}>
                <td>{row.code}</td>
                <td>{ar ? row.labelAr : row.labelEn}</td>
                <td>{row.classification === 'cash' ? (ar ? 'نقدي' : 'Cash') : ar ? 'غير نقدي' : 'Non-cash'}</td>
                <td>{row.disabled ? (ar ? 'نعم' : 'Yes') : ar ? 'لا' : 'No'}</td>
                {editable && (
                  <td>
                    <button
                      type="button"
                      onClick={() => {
                        setMethod({
                          code: row.code,
                          labelAr: row.labelAr,
                          labelEn: row.labelEn,
                          classification: row.classification,
                          disabled: row.disabled,
                        });
                        setEditingMethod(true);
                      }}
                    >
                      {edit} {row.code}
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </section>
      <section className="er-card">
        <h2>{ar ? 'فئات المصروفات' : 'Expense categories'}</h2>
        {editable && (
          <form
            className="er-actions"
            onSubmit={(event) => {
              event.preventDefault();
              void action.run(async (requestKey) => {
                const response = await client.saveExpenseCategory({
                  headers: { 'idempotency-key': requestKey },
                  body: {
                    code: category.code,
                    labelAr: category.labelAr,
                    labelEn: category.labelEn,
                    ...(category.id ? { categoryId: category.id } : {}),
                  },
                });
                if (response.status === 200 || response.status === 201) {
                  refresh();
                  setCategory({ id: '', code: '', labelAr: '', labelEn: '' });
                }
                return response;
              });
            }}
          >
            <Field id="category-code" label={ar ? 'رمز الفئة' : 'Category code'}>
              <input
                id="category-code"
                required
                readOnly={Boolean(category.id)}
                value={category.code}
                onChange={(e) => setCategory({ ...category, code: e.target.value })}
              />
            </Field>
            <Field id="category-ar" label={ar ? 'اسم الفئة بالعربية' : 'Category Arabic label'}>
              <input
                id="category-ar"
                required
                value={category.labelAr}
                onChange={(e) => setCategory({ ...category, labelAr: e.target.value })}
              />
            </Field>
            <Field id="category-en" label={ar ? 'اسم الفئة بالإنجليزية' : 'Category English label'}>
              <input
                id="category-en"
                required
                value={category.labelEn}
                onChange={(e) => setCategory({ ...category, labelEn: e.target.value })}
              />
            </Field>
            <button className="er-button" type="submit" disabled={action.busy}>
              {save}
            </button>
          </form>
        )}
        <table className="er-table">
          <thead>
            <tr>
              <th>{ar ? 'الرمز' : 'Code'}</th>
              <th>{ar ? 'الاسم' : 'Label'}</th>
              <th>{stateLabel}</th>
              {editable && <th>{edit}</th>}
            </tr>
          </thead>
          <tbody>
            {categories.data.map((row) => (
              <tr key={row.id}>
                <td>{row.code}</td>
                <td>{ar ? row.labelAr : row.labelEn}</td>
                <td>{row.disabled ? (ar ? 'نعم' : 'Yes') : ar ? 'لا' : 'No'}</td>
                {editable && (
                  <td>
                    <button
                      type="button"
                      onClick={() =>
                        setCategory({ id: row.id, code: row.code, labelAr: row.labelAr, labelEn: row.labelEn })
                      }
                    >
                      {edit} {row.code}
                    </button>
                    <button
                      type="button"
                      disabled={action.busy}
                      onClick={() => {
                        void action.run(async (requestKey) => {
                          const response = await client.setExpenseCategoryState({
                            path: { categoryId: row.id },
                            headers: { 'idempotency-key': requestKey },
                            body: { disabled: !row.disabled },
                          });
                          if (response.status === 200) refresh();
                          return response;
                        });
                      }}
                    >
                      {row.disabled ? (ar ? 'تفعيل' : 'Enable') : ar ? 'تعطيل' : 'Disable'} {row.code}
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </main>
  );
}
